import { useQuery } from '@tanstack/react-query'
import { CheckCircle2, Info } from 'lucide-react'
import { useEffect, useState } from 'react'

import { savingsProductListQueryOptions } from '@/api/savings-api'
import type { SavingsAccount } from '@/api/savings-types'
import type { CustomerSummary } from '@/api/types'
import { MoneyDisplay } from '@/components/money-display'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { MemberPicker } from '@/features/customers/member-picker'

import { productClassDescription, productClassLabel } from './constants'
import { useOpenSavingsAccount } from './mutations'
import { SavingsActionDialog } from './savings-action-dialog'

const today = () => new Date().toISOString().slice(0, 10)

/**
 * Opening an account.
 *
 * Opening moves no money, which is why it is not a teller action and why the product's minimum
 * opening balance is not enforced here — the server checks it on the first customer payment
 * instead, since an account with no movement on it has not met a minimum of anything. The figure
 * is still shown, because it is what the customer has to bring.
 *
 * The passbook number is optional and is a *record* of a physical book, not an issue of one: a
 * book is registered against the customer through the passbook register, which enforces the serial
 * uniqueness across the whole institution. Typing a serial here associates it with the account.
 */
export function OpenAccountDialog({
  customerId,
  branchId,
  open,
  onOpenChange,
  onOpened,
}: {
  /** Fixed when the dialog is opened from a customer, blank when opened from the account list. */
  customerId: string | null
  branchId: string | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onOpened?: (account: SavingsAccount) => void
}) {
  const [customer, setCustomer] = useState<CustomerSummary | null>(null)
  const [productId, setProductId] = useState('')
  const [openedDate, setOpenedDate] = useState(today)
  const [passbookNumber, setPassbookNumber] = useState('')
  const [opened, setOpened] = useState<SavingsAccount | null>(null)

  const products = useQuery({ ...savingsProductListQueryOptions(), enabled: open })
  const create = useOpenSavingsAccount()

  useEffect(() => {
    if (!open) {
      setCustomer(null)
      setProductId('')
      setOpenedDate(today())
      setPassbookNumber('')
      setOpened(null)
      create.reset()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, customerId])

  const selectable = (products.data ?? []).filter((product) => product.isActive)
  const product = selectable.find((candidate) => candidate.id === productId)

  // Fixed when the dialog was opened from a customer's own screen, chosen here otherwise. The two
  // are the same value to the endpoint and different acts to the operator: one is already decided,
  // the other has to be confirmed by name before an account exists in somebody's name.
  const chosenCustomerId = customerId ?? customer?.id ?? null

  return (
    <SavingsActionDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Open a savings account"
      description="The account number is issued by the server from the branch sequence. It is never typed here."
      outcome={
        opened ? (
          <Alert tone="success">
            <CheckCircle2 aria-hidden />
            <div className="space-y-1">
              <AlertTitle>Account {opened.accountNumber} is open</AlertTitle>
              <AlertDescription>
                {productClassLabel(opened.productClass)} for {opened.customerName}, opened{' '}
                {opened.openedDate}. It holds nothing yet — the first payment is what tests the
                product&apos;s minimum opening balance.
              </AlertDescription>
            </div>
          </Alert>
        ) : null
      }
      confirmLabel="Open account"
      isPending={create.isPending}
      disabled={
        chosenCustomerId === null || productId === '' || openedDate === '' || branchId === null
      }
      error={create.isError ? create.error : null}
      onConfirm={() => {
        if (branchId === null || chosenCustomerId === null) {
          return
        }

        create.mutate(
          {
            customerId: chosenCustomerId,
            productId,
            branchId,
            openedDate,
            passbookNumber: passbookNumber.trim() === '' ? null : passbookNumber.trim(),
          },
          {
            onSuccess: (account) => {
              setOpened(account)
              onOpened?.(account)
            },
          },
        )
      }}
    >
      {branchId === null ? (
        <Alert tone="warning">
          <Info aria-hidden />
          <div className="space-y-1">
            <AlertTitle>No branch is selected</AlertTitle>
            <AlertDescription>
              An account belongs to a branch. Choose one in the header before opening it.
            </AlertDescription>
          </div>
        </Alert>
      ) : null}

      {customerId === null ? (
        <MemberPicker
          id="open-account-customer"
          label="Customer"
          selected={customer}
          onSelect={setCustomer}
          kycStatus="Approved"
          helpText="Two characters or more. Search, then confirm the name. A phone number only matches when it is typed in full — only approved customers can hold an account."
        />
      ) : null}

      <div className="space-y-1.5">
        <Label htmlFor="open-account-product">Product</Label>
        <Select value={productId} onValueChange={setProductId}>
          <SelectTrigger id="open-account-product">
            <SelectValue placeholder={products.isPending ? 'Loading…' : 'Select a product'} />
          </SelectTrigger>
          <SelectContent>
            {selectable.map((candidate) => (
              <SelectItem key={candidate.id} value={candidate.id}>
                {candidate.code} — {candidate.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {products.isError ? (
          <p className="text-xs text-danger-text">
            The product list could not be read, so there is nothing to choose from. Reading it needs
            savings.product.view.
          </p>
        ) : selectable.length === 0 && !products.isPending ? (
          <p className="text-xs text-muted">
            No active products. A retired product keeps its existing accounts and takes no new ones.
          </p>
        ) : product ? (
          <div className="space-y-1 text-xs text-muted">
            <p>{productClassDescription(product.productClass)}</p>
            <p>
              Minimum opening balance{' '}
              <MoneyDisplay
                value={product.minOpeningBalanceMinor}
                currency={product.currencyCode}
                className="inline text-left"
              />
              . It is checked on the first payment, not now.
            </p>
          </div>
        ) : null}
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="open-account-date">Opened date</Label>
        <Input
          id="open-account-date"
          type="date"
          value={openedDate}
          max={today()}
          onChange={(event) => setOpenedDate(event.target.value)}
          aria-describedby="open-account-date-help"
        />
        <p id="open-account-date-help" className="text-xs text-muted">
          An account cannot have been opened on a date that has not happened.
        </p>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="open-account-passbook">Passbook serial (optional)</Label>
        <Input
          id="open-account-passbook"
          value={passbookNumber}
          autoComplete="off"
          spellCheck={false}
          onChange={(event) => setPassbookNumber(event.target.value)}
          aria-describedby="open-account-passbook-help"
        />
        <p id="open-account-passbook-help" className="text-xs text-muted">
          Records the book already in the customer&apos;s hands against this account. Issuing a new
          book is a separate act, in the passbook register, where the serial is checked for
          uniqueness across the whole institution.
        </p>
      </div>
    </SavingsActionDialog>
  )
}
